import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { AdminService } from '@/core/services/AdminService';
import { debugLogger } from '@/utils/debugLogger';

/**
 * Hook centralizado para verificar roles do usuário
 * Usa React Query com cache de 30 segundos para evitar múltiplas chamadas
 */
export const useUserRole = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['userRole', user?.id],
    queryFn: async () => {
      debugLogger.debug('[useUserRole] Starting role check', { userId: user?.id });
      
      if (!user?.id) {
        debugLogger.warn('[useUserRole] No user ID available');
        return { isAdmin: false, isModerator: false, canAccessAdminPanel: false };
      }

      // Uma ÚNICA chamada ao AdminService que faz as verificações necessárias
      const [isAdmin, isModerator] = await Promise.all([
        AdminService.isAdmin(user.id),
        AdminService.isModerator(user.id)
      ]);

      const result = {
        isAdmin,
        isModerator,
        canAccessAdminPanel: isAdmin || isModerator
      };

      debugLogger.debug('[useUserRole] Role check complete', {
        userId: user.id,
        ...result
      });

      return result;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // Cache de 30 segundos (reduzido de 5 minutos)
    gcTime: 10 * 60 * 1000, // Garbage collection após 10 minutos
    retry: 2, // Retry 2x se falhar
    retryDelay: 1000 // 1s entre retries
  });

  const returnValue = {
    isAdmin: data?.isAdmin ?? false,
    isModerator: data?.isModerator ?? false,
    canAccessAdminPanel: data?.canAccessAdminPanel ?? false,
    loading: isLoading
  };

  debugLogger.debug('[useUserRole] Returning values', returnValue);

  return returnValue;
};

/**
 * Invalida o cache de roles do usuário
 * Limpa tanto o cache do React Query quanto do AdminService
 */
export const invalidateUserRoleCache = () => {
  const queryClient = useQueryClient();
  
  // Limpar cache do React Query
  queryClient.invalidateQueries({ queryKey: ['userRole'] });
  
  // Limpar cache do AdminService
  AdminService.clearCache();
  
  debugLogger.info('[useUserRole] Cache invalidated manually');
};
